import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { getPage, getSection, getPrevNext } from "./data/index";
import DocsRenderer, { extractHeadings } from "./DocsRenderer";
import DocsTocPopover from "./DocsTocPopover";

function Breadcrumbs({ sectionLabel, sectionKey, pageTitle }) {
  return (
    <nav className="flex items-center gap-2 mb-6 font-['DM_Sans']" style={{ fontSize: 13 }}>
      <Link to="/docs" className="text-[#94A3B8] hover:text-[#4B7BF5] transition-colors">
        Docs
      </Link>
      <span className="text-[#CBD5E1]">/</span>
      <Link
        to={`/docs/${sectionKey}`}
        className="text-[#94A3B8] hover:text-[#4B7BF5] transition-colors"
      >
        {sectionLabel}
      </Link>
      <span className="text-[#CBD5E1]">/</span>
      <span className="text-[#475569]">{pageTitle}</span>
    </nav>
  );
}

function PrevNextCard({ page, sectionKey, direction }) {
  const isNext = direction === "next";
  return (
    <Link
      to={`/docs/${sectionKey}/${page.slug}`}
      className="group flex flex-col gap-1 rounded-xl px-4 py-3 border transition-all hover:border-[#4B7BF5] hover:shadow-sm"
      style={{
        borderColor: "#E2E8F0",
        background: "#FFFFFF",
        flex: 1,
        minWidth: 0,
        alignItems: isNext ? "flex-end" : "flex-start",
      }}
    >
      <span
        className="font-['DM_Sans'] text-[#94A3B8] flex items-center gap-1"
        style={{ fontSize: 12 }}
      >
        {!isNext && <ArrowLeft size={12} />}
        {isNext ? "Next" : "Previous"}
        {isNext && <ArrowRight size={12} />}
      </span>
      <span
        className="font-['DM_Sans'] font-semibold text-[#0F1B3D] group-hover:text-[#4B7BF5] transition-colors truncate max-w-full"
        style={{ fontSize: 14 }}
      >
        {page.title}
      </span>
    </Link>
  );
}

export default function DocsPage() {
  const { section: sectionKey, slug } = useParams();

  const page = getPage(sectionKey, slug);
  const section = getSection(sectionKey);
  const { prev, next } = getPrevNext(sectionKey, slug);

  if (!page || !section) {
    return (
      <div className="max-w-[720px] mx-auto px-6 py-12">
        <div className="text-center py-20">
          <h1
            className="font-['Syne'] font-bold text-[#0F1B3D] mb-3"
            style={{ fontSize: 28 }}
          >
            Page not found
          </h1>
          <p className="font-['DM_Sans'] text-[#64748B] mb-6" style={{ fontSize: 15 }}>
            The page you're looking for doesn't exist or has been moved.
          </p>
          <Link
            to="/docs"
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-white font-['DM_Sans'] transition-all hover:opacity-90"
            style={{ background: "#4B7BF5", fontSize: 14 }}
          >
            <ArrowLeft size={14} />
            Back to Docs
          </Link>
        </div>
      </div>
    );
  }

  const headings = extractHeadings(page.content);

  return (
    <div className="max-w-[720px] mx-auto px-6 py-10">
      {/* TOC Popover */}
      <DocsTocPopover headings={headings} />

      {/* Breadcrumbs */}
      <Breadcrumbs
        sectionLabel={section.label}
        sectionKey={sectionKey}
        pageTitle={page.title}
      />

      {/* Page header */}
      <div className="mb-8">
        <h1
          className="font-['Syne'] font-bold text-[#0F1B3D] mb-2"
          style={{ fontSize: "clamp(22px, 3vw, 30px)", lineHeight: 1.25 }}
        >
          {page.title}
        </h1>
        {(page.subtitle || page.readTime) && (
          <div className="flex flex-wrap items-center gap-3 mt-2">
            {page.subtitle && (
              <p
                className="font-['DM_Sans'] text-[#64748B]"
                style={{ fontSize: 15, lineHeight: 1.6 }}
              >
                {page.subtitle}
              </p>
            )}
            {page.readTime && (
              <span
                className="inline-flex items-center rounded-full px-2.5 py-0.5 font-['DM_Sans'] shrink-0"
                style={{
                  fontSize: 12,
                  background: "rgba(75,123,245,0.08)",
                  color: "#4B7BF5",
                  border: "1px solid rgba(75,123,245,0.2)",
                }}
              >
                {page.readTime}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <DocsRenderer content={page.content} />

      {/* Prev / Next navigation */}
      {(prev || next) && (
        <div
          className="flex gap-3 mt-12 pt-8"
          style={{ borderTop: "1px solid #E2E8F0" }}
        >
          {prev && (
            <PrevNextCard page={prev} sectionKey={sectionKey} direction="prev" />
          )}
          {!prev && next && <div style={{ flex: 1 }} />}
          {next && (
            <PrevNextCard page={next} sectionKey={sectionKey} direction="next" />
          )}
          {!next && prev && <div style={{ flex: 1 }} />}
        </div>
      )}
    </div>
  );
}
